import {
  IServerSideDatasource,
  IServerSideGetRowsParams,
  IServerSideGetRowsRequest,
} from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import BalanceGridRow from '../types';
import parseAGRequest from 'utils/agGrid/ssrm/parseAGRequest';
import { useAppDispatch } from 'hooks/useAppDispatch';
import {
  fetchAccountsV2Promise,
} from 'reducers/accountsReducer';
import { todaysDateIso8601 } from 'utils/dateTime';
import { fetchMultiAccBalancesPromise } from 'actions/accountsActions';
import Account from 'ts/models/Account.model';
import MultiAccountsBalancesResponse from 'ts/api/multi_accounts_balances';
import { GetAccountsV2Response } from 'ts/api/accounts_v2';

const findBalancesOf = (
  acc: Account,
  balances: MultiAccountsBalancesResponse,
) => {
  return balances?.find((b) => b?.accountId === acc.accountId)?.balances || [];
};

export const mergeAccountTotals = (
  accounts: Account[],
  balances: MultiAccountsBalancesResponse,
) => {
  return accounts?.map((acc: Account) => ({
    ...acc,
    balancesUSD: findBalancesOf(acc, balances),
  }));
};

const useBalanceDatasource = () => {
  const dispatch = useAppDispatch();

  const getAccountsV2 = useCallback(
    async (req: IServerSideGetRowsRequest) => {
      const body = parseAGRequest(req, { transformAttrs: true });
      const result: GetAccountsV2Response = await fetchAccountsV2Promise(
        body,
        dispatch,
      );
      return {
        accounts: result.accounts ?? [],
        count: result.count ?? undefined 
      }
    },
    [dispatch],
  );

  const getMultiAccountsBalances = useCallback(
    async (accounts: Account[]) => {
      const body = {
        accountIds: accounts.map((account: Account) => account.accountId),
        closingPriceDate: todaysDateIso8601(),
      };
      const balances: MultiAccountsBalancesResponse =
        await fetchMultiAccBalancesPromise(body, dispatch);

      return balances || [];
    },
    [dispatch],
  );

  const datasource: IServerSideDatasource = useMemo(
    () => ({
      getRows: async ({
        request,
        success,
      }: IServerSideGetRowsParams<BalanceGridRow>) => {
        const accounts = await getAccountsV2(request);
        const balances = await getMultiAccountsBalances(accounts.accounts);
        const rowData = mergeAccountTotals(accounts.accounts, balances)
        success({
          rowData,
          rowCount : accounts.count
        });
      },
    }),
    [getAccountsV2, getMultiAccountsBalances],
  );

  return datasource;
};

export default useBalanceDatasource;
